import { useState, FormEvent, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';

type Lead = {
  id: string;
  name: string;
  email: string;
  phone: string;
};

export default function Leads() {
  const { tokens } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [totalLeads, setTotalLeads] = useState(0);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const headers = useMemo(
    () => (tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : undefined),
    [tokens?.accessToken]
  );

  const fetchLeads = useCallback(
    async (page: number, pageSize: number) => {
      if (!headers) {
        setLeads([]);
        setTotalLeads(0);
        return;
      }
      setLoading(true);
      setError(null);
      setDialogError(null);
      try {
        const res = await axios.get(
          `${config.apiUrl}/api/leads/all/paged?page=${page}&size=${pageSize}`,
          { headers }
        );
        const payload = res?.data?.content ?? res?.data;
        const contentArray = Array.isArray(payload?.content) ? payload.content : Array.isArray(payload) ? payload : [];
        const total = payload?.totalElements ?? contentArray.length ?? 0;
        setLeads(
          contentArray.map((item: any) => ({
            id: item.id,
            name: item.name,
            email: item.email,
            phone: item.phone
          }))
        );
        setTotalLeads(total);
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || 'Failed to load leads');
      } finally {
        setLoading(false);
      }
    },
    [headers]
  );

  useEffect(() => {
    fetchLeads(paginationModel.page, paginationModel.pageSize);
  }, [fetchLeads, paginationModel.page, paginationModel.pageSize]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setDialogError(null);

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    try {
      if (editingLead) {
        await axios.put(
          `${config.apiUrl}/api/leads/update/${encodeURIComponent(editingLead.id)}`,
          {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim()
          },
          { headers }
        );
        setSuccess('Lead updated successfully');
      } else {
        await axios.post(
          `${config.apiUrl}/api/leads/create`,
          {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim()
          },
          { headers }
        );
        setSuccess('Lead created successfully');
      }
      setName('');
      setEmail('');
      setPhone('');
      setEditingLead(null);
      setCreateDialogOpen(false);
      // refresh first page after creating
      fetchLeads(0, paginationModel.pageSize);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (lead: Lead) => {
    setEditingLead(lead);
    setName(lead.name);
    setEmail(lead.email);
    setPhone(lead.phone);
    setCreateDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!headers || !deleteTarget) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.delete(`${config.apiUrl}/api/leads/delete/${encodeURIComponent(deleteTarget)}`, { headers });
      setSuccess('Lead deleted');
      fetchLeads(paginationModel.page, paginationModel.pageSize);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete lead');
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => setDeleteTarget(null);

  const columns: GridColDef<Lead>[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 140 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" aria-label="edit" onClick={() => handleEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="delete" onClick={() => handleDelete(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" gutterBottom>
          Leads
        </Typography>
        <Button variant="contained" onClick={() => { setEditingLead(null); setName(''); setEmail(''); setPhone(''); setCreateDialogOpen(true); }}>
          New Lead
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Box mt={2}>
        <Typography variant="subtitle1" gutterBottom>
          All Leads
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            autoHeight
            rows={leads}
            columns={columns}
            pagination
            paginationMode="server"
            rowCount={totalLeads}
            pageSizeOptions={[5, 10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableRowSelectionOnClick
          />
        )}
      </Box>

      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); setEditingLead(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{editingLead ? 'Edit Lead' : 'New Lead'}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                fullWidth
              />
            </Stack>
            {dialogError && <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>}
          </DialogContent>
          <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); setEditingLead(null); }}>Cancel</Button>
            <Button type="submit" disabled={loading} variant="contained">
              {editingLead ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={cancelDelete}>
        <DialogTitle>Delete lead?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the selected lead. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button color="error" onClick={confirmDelete} disabled={loading}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
